import { FC, useEffect, useState } from "react";
import {
    Button,
    FormControl,
    IconButton,
    Note,
    Select,
    Stack,
    Text,
    TextLink,
    Tooltip,
} from "@contentful/f36-components";
import { CloudUploadIcon, EditIcon, HelpCircleIcon, InfoCircleIcon } from "@contentful/f36-icons";
import tokens from "@contentful/f36-tokens";
import { useSDK } from "@contentful/react-apps-toolkit";
import { EditGlossariesModal, GlossaryHelpModal, ImportGlossaryModal } from "../";
import { GlossaryId, glossaryLimits } from "../../lib";
import { useFetchDeeplGlossaries } from "../../services";
import { getPlanFromCredentials } from "../../utils";
import styles from "./GlossarySelect.module.css";

const loadingText = "Loading...";
export const noGlossaryText = "None";

interface GlossarySelectProps {
    value?: GlossaryId;
    onChange: React.ChangeEventHandler<HTMLSelectElement>;
    savedValue?: GlossaryId;
}

export const GlossarySelect: FC<GlossarySelectProps> = ({ value, onChange, savedValue }) => {
    const sdk = useSDK();
    const deeplCredentials = sdk.parameters.installation.deeplApiKey;
    const plan = getPlanFromCredentials(deeplCredentials);
    const glossaryLimit = glossaryLimits[plan];

    const [isImportModalShown, setImportModalShown] = useState(false);
    const [isEditModalShown, setEditModalShown] = useState(false);
    const [isHelpModalShown, setHelpModalShown] = useState(false);
    const { data: glossaries, isLoading: isGlossariesLoading } = useFetchDeeplGlossaries();

    /*  Automatically unselects the current glossary if it doesn't exist anymore */
    useEffect(() => {
        if (glossaries && savedValue && !glossaries.find(glossary => glossary.id === savedValue)) {
            onChange({
                target: {
                    value: noGlossaryText,
                },
            } as React.ChangeEvent<HTMLSelectElement>);
        }
    }, [glossaries, savedValue, onChange]);

    const noGlossaries = !glossaries || glossaries.length === 0;

    return (
        <FormControl marginBottom="none">
            <Stack justifyContent="space-between" alignItems="center">
                <FormControl.Label style={{ color: tokens.purple500 }} marginBottom="none">
                    Glossary
                </FormControl.Label>
                <Stack alignItems="center" spacing="none">
                    <Text fontColor="purple500">
                        <Text fontColor="purple500" fontWeight="fontWeightDemiBold">
                            {glossaries?.length}/{glossaryLimit}
                        </Text>{" "}
                        {glossaryLimit === 1 ? "glossary" : "glossaries"} used
                    </Text>
                    <IconButton
                        aria-label="Learn more about glossaries in Linguiny"
                        icon={<HelpCircleIcon variant="premium" />}
                        onClick={() => setHelpModalShown(true)}
                    />
                    <GlossaryHelpModal
                        isShown={isHelpModalShown}
                        onClose={() => setHelpModalShown(false)}
                    />
                </Stack>
            </Stack>
            <Stack flexDirection="column" alignItems="start" spacing="spacingS">
                <Note variant="premium" icon={<InfoCircleIcon />} style={{ width: "100%" }}>
                    To use the glossary feature, you need a{" "}
                    <TextLink
                        href="https://www.deepl.com/pro#team"
                        target="_blank"
                        rel="noopener noreferrer">
                        DeepL Pro Advanced
                    </TextLink>{" "}
                    subscription or higher.
                </Note>
                <Select
                    value={isGlossariesLoading ? loadingText : value}
                    onChange={onChange}
                    className={styles.select}
                    isDisabled={isGlossariesLoading || !glossaries}>
                    {isGlossariesLoading && <Select.Option>{loadingText}</Select.Option>}
                    <Select.Option>{noGlossaryText}</Select.Option>
                    {glossaries?.map((glossary, index) => (
                        <Select.Option key={index} value={glossary.id}>
                            {glossary.name}
                        </Select.Option>
                    ))}
                </Select>

                <Stack spacing="spacingXs">
                    <Tooltip
                        content={
                            glossaries && glossaries.length >= glossaryLimit ? (
                                <span>
                                    Glossary limit reached. DeepL API{" "}
                                    <span className={styles.capitalize}>{plan}</span> allows for{" "}
                                    {glossaryLimit}{" "}
                                    {glossaryLimit === 1 ? "glossary" : "glossaries"}.
                                </span>
                            ) : undefined
                        }>
                        <Button
                            variant="primary"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => setImportModalShown(true)}
                            isDisabled={
                                isGlossariesLoading ||
                                !glossaries ||
                                glossaries.length >= glossaryLimit
                            }
                            isLoading={isGlossariesLoading}
                            style={{ backgroundColor: tokens.purple500 }}>
                            Import
                        </Button>
                    </Tooltip>
                    {glossaries && (
                        <ImportGlossaryModal
                            isShown={isImportModalShown}
                            setIsShown={setImportModalShown}
                            glossaries={glossaries}
                        />
                    )}
                    <Tooltip content={noGlossaries ? "Import a glossary to edit it" : undefined}>
                        <Button
                            variant="secondary"
                            startIcon={<EditIcon />}
                            onClick={() => setEditModalShown(true)}
                            isDisabled={isGlossariesLoading || noGlossaries}
                            isLoading={isGlossariesLoading}>
                            Edit
                        </Button>
                    </Tooltip>
                    {glossaries && (
                        <EditGlossariesModal
                            isShown={isEditModalShown}
                            setIsShown={setEditModalShown}
                            glossaries={glossaries}
                            savedGlossaryId={savedValue}
                        />
                    )}
                </Stack>
            </Stack>
        </FormControl>
    );
};
